import type { FileMetadataMap } from '@Core/lib/tasks/utils/fileTypes';
import { router as inertiaRouter } from '@inertiajs/react';
import { AiComponentCompileError } from '@Pages/pageRuntime/compiler/AiComponentCompileError';
import type {
  CompileAiComponentOptions,
  CompiledAiComponent,
  PageRuntimeSourceKind
} from '@Pages/pageRuntime/compiler/compileAiComponent';
import { pageCompiler } from '@Pages/pageRuntime/compiler/pageCompiler';
import { FileMetadataContext } from '@Pages/pageRuntime/context/FileMetadataContext';
import { componentSourcePath } from '@Pages/pageRuntime/designMode/sourceMetadata';
import { createFileMetadataStore, EMPTY_FILES } from '@Pages/pageRuntime/fileMetadataStore';
import {
  createImagePriorityGrant,
  createPageImageRuntime,
  PageImageRuntimeContext
} from '@Pages/pageRuntime/imageRuntime';
import {
  PageRuntimeContext,
  type PageRuntime,
  type PageTransformMode
} from '@Pages/pageRuntime/pageRuntimeContextValue';
import { prepareOptionalRuntime } from '@Pages/pageRuntime/prepareOptionalRuntime';
import { buildSdkCore, withCustomComponents } from '@Pages/pageRuntime/sdk/buildSdk';
import {
  createPageSdkRequestTracker,
  type PageSdkRequestTracker
} from '@Pages/pageRuntime/sdk/requestActivity';
import type { RouterLike } from '@Pages/pageRuntime/sdk/router';
import type {
  SdkAuthConfig,
  SdkNavigationMenuCatalog,
  PageResourceData,
  PageRouteSettings,
  SdkIdentity,
  SdkWorkspaceField,
  SdkWorkspaceRecord
} from '@Pages/pageRuntime/sdk/types';
import { createWorkspaceValuesStore } from '@Pages/pageRuntime/sdk/workspaceValuesStore';
import {
  forwardingSharedComponent,
  type SharedComponentImplementations
} from '@Pages/pageRuntime/sharedComponentForwarding';
import { SharedComponentImplementationsProvider } from '@Pages/pageRuntime/SharedComponentImplementationsProvider';
import { withOptionalRuntime } from '@Pages/pageRuntime/withOptionalRuntime';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { SystemPermission } from '@/types/enums';

import type { PageWorkflowRouteSettings } from './context/WorkflowRouteSettingsContext';
import {
  WorkspaceFieldsContext,
  type WorkspaceFieldsRuntime
} from './context/WorkspaceFieldsContext';

export type SharedComponentEntry = {
  name: string;
  code: string;
};

export type PageRuntimeInputs = {
  files?: FileMetadataMap;
  /** Workspace hosts whose absolute `/files/{uuid}` URLs the image runtime may resize. */
  fileHosts?: string[];
  /** Loads every runtime-managed image eagerly, as a printed document requires. */
  eagerImages?: boolean;
  runtimeKey?: string;
  data: Record<string, unknown>;
  resources?: Record<string, PageResourceData>;
  resourceIdsBySlug?: Record<string, string>;
  authoringSourcePath?: string;
  workspace?: SdkWorkspaceRecord | null;
  workspaceFields?: SdkWorkspaceField[] | null;
  navigation?: SdkNavigationMenuCatalog | null;
  identity?: SdkIdentity;
  auth?: SdkAuthConfig | null;
  sharedComponents?: SharedComponentEntry[];
  workflowRouteSettings?: PageWorkflowRouteSettings;
  pageRouteSettings?: PageRouteSettings;
  requestTracker?: PageSdkRequestTracker;
  /** Defaults to Inertia's router. The page playground passes one that blocks every visit. */
  router?: RouterLike;
};

declare global {
  interface Window {
    __diagonalPageRuntimePayloadMetrics?: {
      sharedComponentCount: number;
      sharedComponentSourceBytes: number;
    };
  }
}

/**
 * Pages resolve shared components by name at render time, so a component that
 * was renamed, unpublished, or deleted leaves the page's `Sdk.Custom.<Name>`
 * reference absent. Reading an absent name returns a forwarding component that
 * throws a named diagnostic, so the page error fallback reports the missing
 * component instead of React failing on an `undefined` element type.
 *
 * Only PascalCase names are treated as component reads; everything else falls
 * through so object internals (`toString`, `then`, …) keep their behavior.
 * Code component names are validated as PascalCase at creation, rename,
 * import, and publish, so every reachable `Sdk.Custom.<Name>` miss lands here.
 */
function customComponentRegistry(
  components: Record<string, React.ComponentType>
): Record<string, React.ComponentType> {
  return new Proxy(components, {
    get(target, key, receiver) {
      if (typeof key !== 'string' || key in target || !/^[A-Z]/.test(key)) {
        return Reflect.get(target, key, receiver);
      }

      return forwardingSharedComponent(key);
    }
  });
}

const EditorPreviewRuntimeProvider = lazy(async () => ({
  default: (await import('@Pages/pageRuntime/EditorPreviewRuntimeProvider'))
    .EditorPreviewRuntimeProvider
}));

function ScopedPageRuntimeProvider({
  files = EMPTY_FILES,
  fileHosts,
  eagerImages = false,
  children,
  rootSources,
  data,
  resources,
  resourceIdsBySlug,
  authoringSourcePath,
  workspace,
  workspaceFields,
  navigation,
  identity,
  auth,
  sharedComponents,
  workflowRouteSettings,
  pageRouteSettings,
  requestTracker,
  router = inertiaRouter,
  transformMode = 'main-thread',
  compilePreviewStylesheet = false
}: PageRuntimeInputs & {
  children: React.ReactNode;
  rootSources: Array<string | null | undefined>;
  transformMode?: PageTransformMode;
  /**
   * Whether this document's Tailwind CSS is the worker's to compile. True only
   * on a draft-preview document, whose shell emits an empty placeholder
   * `<style>` in place of a workspace stylesheet link.
   */
  compilePreviewStylesheet?: boolean;
}) {
  const queryClient = useQueryClient();
  const [fileStore] = useState(() => createFileMetadataStore(files));
  useEffect(() => {
    fileStore.activate();
    return () => {
      fileStore.deactivate();
      queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes(fileStore.scope)
      });
    };
  }, [fileStore, queryClient]);
  useLayoutEffect(() => {
    fileStore.replace('initial', [{ files, revision: 0 }]);
  }, [fileStore, files]);
  const [imagePriority] = useState(createImagePriorityGrant);
  const fileHostsKey = (fileHosts ?? []).join('\u0000');
  // Images compare this value by identity, so it changes only with its inputs.
  // react-doctor-disable-next-line react-compiler-no-manual-memoization
  const imageRuntime = useMemo(
    () =>
      createPageImageRuntime({
        fileHosts: fileHostsKey === '' ? [] : fileHostsKey.split('\u0000'),
        priority: imagePriority,
        eagerLoading: eagerImages
      }),
    [eagerImages, fileHostsKey, imagePriority]
  );
  // A tracker belongs to one runtime: the page shell remounts this provider per
  // page, so an owned tracker cannot carry request activity into the next page.
  const [ownedRequestTracker] = useState(createPageSdkRequestTracker);
  const sdkRequestTracker = requestTracker ?? ownedRequestTracker;
  const isWorkerMode = transformMode === 'worker';

  useEffect(() => {
    window.__diagonalPageRuntimePayloadMetrics = {
      sharedComponentCount: sharedComponents?.length ?? 0,
      sharedComponentSourceBytes: new Blob(
        (sharedComponents ?? []).map((component) => component.code)
      ).size
    };
  }, [sharedComponents]);

  // In worker mode the committed generation decides what may render, so a draft
  // edit must not suspend the tree waiting for chunks it may never need.
  if (!isWorkerMode) {
    prepareOptionalRuntime(rootSources);
  }

  // User-authored component factories and opaque compiler calls must retain their identities and
  // compile caches until a runtime input changes; React Compiler cannot infer that requirement.
  // react-doctor-disable-next-line react-compiler-no-manual-memoization
  const sdkState = useMemo(() => {
    const sdkCore = buildSdkCore({
      fileStore,
      data,
      resources: resources ?? {},
      resourceIdsBySlug,
      workspace,
      workspaceFields,
      navigation,
      identity,
      auth,
      axios,
      router,
      workflowRouteSettings,
      pageRouteSettings,
      queryClient,
      requestTracker: sdkRequestTracker
    });
    const customComponents: Record<string, React.ComponentType> = {};

    for (const component of sharedComponents ?? []) {
      customComponents[component.name] = forwardingSharedComponent(component.name);
    }

    return withCustomComponents(sdkCore, customComponentRegistry(customComponents));
  }, [
    auth,
    fileStore,
    data,
    identity,
    navigation,
    pageRouteSettings,
    queryClient,
    resourceIdsBySlug,
    resources,
    router,
    sdkRequestTracker,
    sharedComponents,
    workflowRouteSettings,
    workspace,
    workspaceFields
  ]);

  // The values store keeps one identity per workspace payload so a save survives
  // every other runtime change. A partial reload of `pageData` keeps the
  // `workspace` prop reference, so keying on `workspace` alone is what preserves
  // the saved values; a compiler bail-out must not hand out a fresh store either.
  // react-doctor-disable-next-line react-compiler-no-manual-memoization
  const workspaceValuesStore = useMemo(
    () => createWorkspaceValuesStore(workspace?.values),
    [workspace]
  );

  const workspaceFieldsRuntime: WorkspaceFieldsRuntime = {
    values: workspaceValuesStore,
    fields: sdkState.workspace?.fields ?? {},
    canUpdate:
      sdkState.permissions.has(SystemPermission.UpdateWorkspaceFields) ||
      sdkState.permissions.has(SystemPermission.ManageWorkspaceFields)
  };

  // react-doctor-disable-next-line react-compiler-no-manual-memoization
  const mainThreadState = useMemo<{
    compile: (
      source: string | null | undefined,
      kind: PageRuntimeSourceKind,
      options?: Omit<CompileAiComponentOptions, 'kind'>
    ) => CompiledAiComponent;
    implementations: SharedComponentImplementations;
  }>(() => {
    const Sdk = sdkState;
    const implementations: SharedComponentImplementations = {};
    const sharedCompileCache = new Map<string, CompiledAiComponent>();
    const rootCompileCache = new Map<
      Exclude<PageRuntimeSourceKind, 'shared'>,
      {
        source: string;
        compiled: CompiledAiComponent;
      }
    >();
    const compile = (
      source: string | null | undefined,
      kind: PageRuntimeSourceKind,
      options: Omit<CompileAiComponentOptions, 'kind'> = {}
    ) => {
      const compileAiComponent = pageCompiler();

      if (!source) {
        return compileAiComponent(source, Sdk, { ...options, kind });
      }

      const cacheKey = `${options.sourcePath ?? ''}\u0000${options.authoringFilePath ?? ''}\u0000${source}`;

      if (kind !== 'shared') {
        const cached = rootCompileCache.get(kind);

        if (cached?.source === cacheKey) {
          return cached.compiled;
        }

        const compiled = compileAiComponent(source, Sdk, {
          ...options,
          kind
        });
        rootCompileCache.set(kind, { source: cacheKey, compiled });

        return compiled;
      }

      const cached = sharedCompileCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const compiled = compileAiComponent(source, Sdk, {
        ...options,
        kind
      });
      sharedCompileCache.set(cacheKey, compiled);

      return compiled;
    };

    if (isWorkerMode) {
      return { compile, implementations };
    }

    for (const component of sharedComponents ?? []) {
      const sourcePath = componentSourcePath(component.name);
      const result = compile(component.code, 'shared', {
        ...(authoringSourcePath ? { authoringFilePath: sourcePath } : {}),
        sourcePath
      });

      if (result.Component) {
        implementations[component.name] = withOptionalRuntime(result.Component, component.code);
      } else if (result.error) {
        implementations[component.name] = new AiComponentCompileError(
          `Sdk.Custom.${component.name} failed to compile: ${result.error.message}`,
          {
            cause: result.error,
            sourcePath: result.error.sourcePath ?? sourcePath
          }
        );
      }
    }

    return { compile, implementations };
  }, [authoringSourcePath, isWorkerMode, sdkState, sharedComponents]);

  const renderProviders = (
    runtime: PageRuntime,
    implementations: SharedComponentImplementations,
    notice: React.ReactNode = null
  ) => (
    <FileMetadataContext.Provider value={fileStore}>
      <PageImageRuntimeContext.Provider value={imageRuntime}>
        <SharedComponentImplementationsProvider implementations={implementations}>
          <PageRuntimeContext.Provider value={runtime}>
            <WorkspaceFieldsContext.Provider value={workspaceFieldsRuntime}>
              {notice}
              {children}
            </WorkspaceFieldsContext.Provider>
          </PageRuntimeContext.Provider>
        </SharedComponentImplementationsProvider>
      </PageImageRuntimeContext.Provider>
    </FileMetadataContext.Provider>
  );

  if (isWorkerMode) {
    return (
      <Suspense fallback={null}>
        <EditorPreviewRuntimeProvider
          Sdk={sdkState}
          sharedComponents={sharedComponents}
          authoring={Boolean(authoringSourcePath)}
          stylesheet={compilePreviewStylesheet}
        >
          {({ authoringRuntime, implementations, notice }) =>
            renderProviders(
              {
                Sdk: sdkState,
                requestTracker: sdkRequestTracker,
                router,
                transformMode: 'worker',
                authoring: authoringRuntime
              },
              implementations,
              notice
            )
          }
        </EditorPreviewRuntimeProvider>
      </Suspense>
    );
  }

  return renderProviders(
    {
      Sdk: sdkState,
      requestTracker: sdkRequestTracker,
      router,
      transformMode: 'main-thread',
      compile: mainThreadState.compile
    },
    mainThreadState.implementations
  );
}

export function PageRuntimeProvider(props: React.ComponentProps<typeof ScopedPageRuntimeProvider>) {
  const scopeKey = JSON.stringify([
    props.runtimeKey,
    props.workspace?.id,
    props.identity,
    props.auth
  ]);
  return <ScopedPageRuntimeProvider key={scopeKey} {...props} />;
}
